import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QuantityControl } from "@/components/ui/QuantityControl";
import { Skeleton } from "@/components/ui/skeleton";
import type { OfferPriceState } from "@/utils/offerPriceState";
import { PriceStatusSlot } from "../OfferPriceStatus";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { SalonStockerHttpTypes } from "@repo/api-sdk";
import { Package, ShoppingCart, X, Zap } from "lucide-react";
import type { FC, ReactNode } from "react";
import React from "react";

export interface ProductDetailDialogHeaderProps {
  // Static props from parent (from ProductCardWithPriceComparison)
  sellerName: string;
  productName: string;
  thumbnail: string | null;

  // Dynamic data from hook
  priceCalculatedFormatted: string;
  priceCalculatedAmount: number;
  priceOriginalFormatted?: string;
  savingsFormatted?: string;
  savingsPercentage?: string;
  status: SalonStockerHttpTypes.StoreSellerOfferStatus;
  isLoading: boolean;
  /**
   * Anything but `ready` replaces the price (and the total built from it) with a
   * loader or an error and disables buying: the price is a claimed seller's
   * trade price that has not arrived (KAN-343).
   */
  priceState?: OfferPriceState;
  isAddingToCart: boolean;

  // Quantity & actions
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onAddToCart: () => void;
  onQuickBuy: () => void;
  onClose: () => void;
  /** Secondary action rendered alongside the buy buttons (e.g. Add to list). */
  addToListSlot?: ReactNode;
}

const ProductDetailDialogHeader: FC<ProductDetailDialogHeaderProps> =
  React.memo((props) => {
    const {
      sellerName,
      productName,
      thumbnail,
      priceCalculatedFormatted,
      priceCalculatedAmount,
      priceOriginalFormatted,
      savingsFormatted,
      savingsPercentage,
      status,
      isLoading,
      priceState = "ready",
      isAddingToCart,
      quantity,
      onQuantityChange,
      onAddToCart,
      onQuickBuy,
      onClose,
      addToListSlot
    } = props;

    const isPriceHeld = priceState !== "ready";

    const isOutOfStock =
      status === SalonStockerHttpTypes.StoreSellerOfferStatus.OUT_OF_STOCK;

    const totalAmount = priceCalculatedAmount * quantity;
    const totalFormatted = new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD"
    }).format(totalAmount);

    return (
      <div className="relative min-h-[140px] bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
          aria-label="Close dialog"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Below `sm` the buy controls drop under the product instead of beside
            it: side by side they ran off the right edge of a phone, taking Quick
            Buy and Add Cart out of reach. */}
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex min-w-0 flex-1 gap-3 sm:gap-4">
            {/* Product Image */}
            <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-xl bg-white shadow-lg sm:h-28 sm:w-28 lg:h-36 lg:w-36">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={productName}
                  className="h-full w-full rounded-xl object-cover"
                />
              ) : (
                <Package className="h-12 w-12 text-purple-600 lg:h-16 lg:w-16" />
              )}
            </div>

            {/* Product Info */}
            <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
              <div>
                {/* Supplier Name - the only part of the column that reaches the
                    absolutely positioned close button, so only it keeps clear of
                    it. The product name below sits under the button and gets
                    the full width. */}
                <div className="pr-10 sm:pr-0">
                  <p className="text-xs font-medium italic text-yellow-100">
                    Order with
                  </p>
                  <p className="text-sm font-bold uppercase italic text-yellow-100">
                    {sellerName}
                  </p>
                </div>

                {/* Product Name */}
                <TruncateTooltip
                  as="h2"
                  lines={3}
                  className="mt-1 text-lg font-bold leading-tight lg:text-2xl"
                >
                  {productName}
                </TruncateTooltip>

                {/* Out of Stock Badge */}
                {isOutOfStock && (
                  <Badge variant="critical" className="mt-2">
                    Out of Stock
                  </Badge>
                )}
              </div>

              {/* Price Section */}
              <div className="mt-2">
                {isLoading ? (
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-16 bg-white/20" />
                    <Skeleton className="h-6 w-24 bg-white/20" />
                  </div>
                ) : isPriceHeld ? (
                  <PriceStatusSlot state={priceState} onDark />
                ) : (
                  <>
                    {priceOriginalFormatted && (
                      <p className="text-sm text-purple-200 line-through">
                        {priceOriginalFormatted}
                      </p>
                    )}
                    <p className="text-xl font-bold">
                      {priceCalculatedFormatted}
                      {/* Same "each" qualifier used on CartLineItem/SummaryLineItem, small so it
                        doesn't compete with the price, but present so this reads as the
                        per-unit price once Total (right column) diverges from it at qty > 1 */}
                      <span className="ml-1 text-xs font-normal text-purple-200">
                        each
                      </span>
                    </p>
                    {savingsFormatted && savingsPercentage && (
                      <p className="text-sm text-green-300">
                        Save {savingsFormatted} ({savingsPercentage}%)
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* CTA Section - Right Side (under the product on phones) */}
          {/* sm:pt-8 clears the absolutely positioned close button (right-3 top-3, h-8) beside it */}
          <div className="flex flex-col items-end gap-3 sm:justify-between sm:gap-0 sm:pt-8">
            {/* Total */}
            <div className="text-right">
              <p className="text-xs text-purple-200">Total:</p>
              {isPriceHeld ? (
                <PriceStatusSlot state={priceState} showLabel={false} onDark />
              ) : (
                <p className="text-lg font-bold lg:text-xl">{totalFormatted}</p>
              )}
            </div>

            {/* Quantity and Buttons */}
            <div className="flex flex-col items-end gap-2">
              {/* Quantity Control - the +/- stepper is self-explanatory, no caption needed */}
              <QuantityControl
                quantity={quantity}
                onQuantityChange={onQuantityChange}
                min={1}
                size="lg"
                quickSelectOptions={[1, 2, 3, 5, 10, 20]}
              />

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-end gap-2">
                {addToListSlot}

                {/* Secondary action: same white chip as Add to list, plus a small amber
                    accent (icon + border tint) so it still reads as distinct from Add to
                    list without competing with Add to Cart's saturated fill. */}
                <Button
                  variant="outline"
                  onClick={onQuickBuy}
                  disabled={
                    isOutOfStock || isLoading || isPriceHeld || isAddingToCart
                  }
                  className={cn(
                    "h-9 gap-1.5 border-amber-400 bg-white/90 text-xs font-semibold text-gray-900 hover:bg-white",
                    isOutOfStock && "cursor-not-allowed opacity-50"
                  )}
                >
                  <Zap className="h-4 w-4 text-amber-500" />
                  Quick Buy
                </Button>

                {/* min-w-28 matches the lg QuantityControl's natural width (w-8 + w-8 + w-12) above it */}
                <Button
                  onClick={onAddToCart}
                  disabled={
                    isOutOfStock || isLoading || isPriceHeld || isAddingToCart
                  }
                  className={cn(
                    "h-9 min-w-28 gap-1.5 text-xs font-semibold",
                    isOutOfStock
                      ? "cursor-not-allowed bg-gray-400"
                      : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                  )}
                >
                  <ShoppingCart className="h-4 w-4" />
                  Add to cart
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });

ProductDetailDialogHeader.displayName = "ProductDetailDialogHeader";

export { ProductDetailDialogHeader };
